import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Badge,
  Alert,
} from "react-bootstrap";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

// For Excel export
const XLSX = require("xlsx");

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const RepeatServiceAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState({
    vendors: [],
    totalVendors: 0,
    totalServices: 0,
    totalRepeatCustomers: 0,
    totalNewCustomers: 0,
    averageRepeatRatio: 0,
  });
  const [filters, setFilters] = useState({
    pincode: "",
    society: "",
    serviceType: "all",
  });
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    pincode: "",
    society: "",
    serviceType: "all",
  });
  const [pincodeSuggestions, setPincodeSuggestions] = useState([]);
  const [societyNameSuggestions, setSocietyNameSuggestions] = useState([]);
  const [showPincodeSuggestions, setShowPincodeSuggestions] = useState(false);
  const [showSocietyNameSuggestions, setShowSocietyNameSuggestions] =
    useState(false);

  // Fetch data when applied filters change
  useEffect(() => {
    fetchAnalyticsData();
  }, [appliedFilters]);

  useEffect(() => {
    fetchServiceTypes();
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      // Use dynamic base URL approach like other admin components
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const headers = {
        "Content-Type": "application/json",
        ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
      };

      let lastErr = null;

      // Fetch pincodes
      for (const base of bases) {
        try {
          const pincodeResponse = await fetch(`${base}/api/vendors/pincodes`, {
            headers,
          });
          if (pincodeResponse.ok) {
            const pincodeData = await pincodeResponse.json();
            setPincodeSuggestions(pincodeData.pincodes || []);
            break;
          } else {
            lastErr = new Error(`Failed to load pincodes from ${base}`);
            continue;
          }
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      // Fetch society names
      for (const base of bases) {
        try {
          const societyResponse = await fetch(
            `${base}/api/vendors/society-names`,
            { headers }
          );
          if (societyResponse.ok) {
            const societyData = await societyResponse.json();
            setSocietyNameSuggestions(societyData.societyNames || []);
            break;
          } else {
            lastErr = new Error(`Failed to load society names from ${base}`);
            continue;
          }
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (lastErr) {
        console.error("Failed to fetch suggestions:", lastErr);
      }
    } catch (err) {
      console.error("Failed to fetch suggestions:", err);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      // Use dynamic base URL approach like other admin components
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      let data = null;
      let lastErr = null;

      // Try to fetch service types from each base URL until one works
      for (const base of bases) {
        try {
          const response = await fetch(
            `${base}/api/vendors/service-categories`,
            {
              headers: {
                "Content-Type": "application/json",
                ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
              },
            }
          );

          if (response.ok) {
            data = await response.json();
            break;
          } else {
            lastErr = new Error(`Failed to load service types from ${base}`);
            continue;
          }
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!data)
        throw (
          lastErr || new Error("Failed to load service types from any endpoint")
        );

      setServiceTypes(data.categories || []);
    } catch (err) {
      console.error("Failed to fetch service types:", err);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use dynamic base URL approach like other admin components
      const bases = Array.from(
        new Set(
          [
            process.env.REACT_APP_API_BASE_URL,
            "https://api.quvikme.com",
          ].filter(Boolean)
        )
      );

      const adminToken =
        localStorage.getItem("adminToken") ||
        sessionStorage.getItem("adminToken");

      const queryParams = new URLSearchParams();
      if (appliedFilters.pincode)
        queryParams.append("pincode", appliedFilters.pincode);
      if (appliedFilters.society)
        queryParams.append("society", appliedFilters.society);
      if (appliedFilters.serviceType && appliedFilters.serviceType !== "all")
        queryParams.append("serviceType", appliedFilters.serviceType);

      console.log(
        "Fetching analytics data with params:",
        Object.fromEntries(queryParams)
      );

      let data = null;
      let lastErr = null;

      // Try to fetch analytics data from each base URL until one works
      for (const base of bases) {
        try {
          const response = await fetch(
            `${base}/api/vendors/repeat-analytics?${queryParams}`,
            {
              headers: {
                "Content-Type": "application/json",
                ...(adminToken && { Authorization: `Bearer ${adminToken}` }),
              },
            }
          );

          if (!response.ok) {
            const errorText = await response.text();
            lastErr = new Error(
              `HTTP ${response.status} from ${base}: ${errorText}`
            );
            continue;
          }

          data = await response.json();
          break;
        } catch (e) {
          lastErr = e;
          continue;
        }
      }

      if (!data)
        throw (
          lastErr ||
          new Error("Failed to load analytics data from any endpoint")
        );

      console.log("Analytics data received:", data);

      // Log the structure of the first vendor for debugging
      if (data.vendors && data.vendors.length > 0) {
        console.log("First vendor structure:", data.vendors[0]);
      }

      setAnalyticsData(data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching analytics data:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Show suggestions based on input
    if (name === "pincode") {
      setShowPincodeSuggestions(true);
    } else if (name === "society") {
      setShowSocietyNameSuggestions(true);
    }

    // Log filter changes for debugging
    console.log(`Filter changed: ${name} = ${value}`);
  };

  const handlePincodeSuggestionClick = (pincode) => {
    setFilters((prev) => ({
      ...prev,
      pincode: pincode,
    }));
    setShowPincodeSuggestions(false);
  };

  const handleSocietyNameSuggestionClick = (societyName) => {
    setFilters((prev) => ({
      ...prev,
      society: societyName,
    }));
    setShowSocietyNameSuggestions(false);
  };

  const handleFilterBlur = (e) => {
    // Hide suggestions when focus is lost
    const { name } = e.target;
    if (name === "pincode") {
      setTimeout(() => setShowPincodeSuggestions(false), 200);
    } else if (name === "society") {
      setTimeout(() => setShowSocietyNameSuggestions(false), 200);
    }
  };

  const handleApplyFilters = () => {
    // Apply filters when user clicks Apply button
    console.log("Applying filters:", filters);
    setAppliedFilters(filters);
  };

  const handleKeyPress = (e) => {
    // Apply filters when user presses Enter
    if (e.key === "Enter") {
      console.log("Applying filters via Enter key:", filters);
      setAppliedFilters(filters);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      pincode: "",
      society: "",
      serviceType: "all",
    });
    // Also reset applied filters to trigger data refresh
    setAppliedFilters({
      pincode: "",
      society: "",
      serviceType: "all",
    });
  };

  const downloadTopVendorsExcel = () => {
    // Prepare data for Excel export
    const filteredVendors = analyticsData.vendors
      .filter((vendor) => {
        // If service type filter is applied, only show vendors with bookings
        if (
          appliedFilters.serviceType &&
          appliedFilters.serviceType !== "all"
        ) {
          return vendor.totalServices > 0;
        }
        return true;
      })
      .sort((a, b) => b.repeatRatio - a.repeatRatio)
      .slice(0, 5);

    // Format data for Excel
    const excelData = filteredVendors.map((vendor, index) => ({
      Rank: index + 1,
      Vendor:
        vendor.businessName ||
        vendor.name ||
        vendor.vendor?.personal_info?.name ||
        vendor.vendor?.businessName ||
        "N/A",
      Society:
        vendor.societyName ||
        vendor.vendor?.personal_info?.societyName ||
        "N/A",
      Pincode: vendor.pincode || vendor.vendor?.personal_info?.pincode || "N/A",
      Category:
        vendor.category ||
        vendor.businessCategory ||
        vendor.vendor?.categories?.[0]?.category_name ||
        "General",
      Services: vendor.totalServices,
      "Repeat Count": vendor.repeatCustomers,
      "Repeat %": `${vendor.repeatRatio}%`,
      "Repeating Services":
        vendor.repeatingServices && vendor.repeatingServices.length > 0
          ? vendor.repeatingServices
              .map((service) => `${service.name} (${service.count})`)
              .join("; ")
          : "None",
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Top Vendors by Repeat Ratio");

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const filename = `top-vendors-repeat-ratio-${timestamp}.xlsx`;

    // Export to Excel
    XLSX.writeFile(wb, filename);
  };

  const downloadAllVendorsExcel = () => {
    // Prepare data for Excel export
    const filteredVendors = analyticsData.vendors.filter((vendor) => {
      // If service type filter is applied, only show vendors with bookings
      if (appliedFilters.serviceType && appliedFilters.serviceType !== "all") {
        return vendor.totalServices > 0;
      }
      return true;
    });

    // Format data for Excel
    const excelData = filteredVendors.map((vendor, index) => ({
      "Vendor Name":
        vendor.businessName ||
        vendor.name ||
        vendor.vendor?.personal_info?.name ||
        vendor.vendor?.businessName ||
        "N/A",
      "Society Name":
        vendor.societyName ||
        vendor.vendor?.personal_info?.societyName ||
        "N/A",
      Pincode: vendor.pincode || vendor.vendor?.personal_info?.pincode || "N/A",
      Category:
        vendor.category ||
        vendor.businessCategory ||
        vendor.vendor?.categories?.[0]?.category_name ||
        "General",
      "Total Services": vendor.totalServices,
      "Repeat Count": vendor.repeatCustomers,
      "Repeat %": `${vendor.repeatRatio}%`,
      "Repeating Services":
        vendor.repeatingServices && vendor.repeatingServices.length > 0
          ? vendor.repeatingServices
              .map(
                (service) =>
                  `${service.name} (${service.count} bookings, ${service.repeatRatio}%)`
              )
              .join("; ")
          : "No repeating services",
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Adjust column widths
    const colWidths = [
      { wch: 10 }, // Vendor Name
      { wch: 15 }, // Society Name
      { wch: 10 }, // Pincode
      { wch: 15 }, // Category
      { wch: 10 }, // Total Services
      { wch: 10 }, // Repeat Count
      { wch: 10 }, // Repeat %
      { wch: 30 }, // Repeating Services
    ];
    ws["!cols"] = colWidths;

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "All Vendors Analytics");

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const filename = `all-vendors-analytics-${timestamp}.xlsx`;

    // Export to Excel
    XLSX.writeFile(wb, filename);
  };

  const chartData = {
    labels: ["Repeat Services", "New Services"],
    datasets: [
      {
        data: [
          analyticsData.totalRepeatCustomers,
          analyticsData.totalNewCustomers,
        ],
        backgroundColor: ["#4e73df", "#1cc88a"],
        hoverBackgroundColor: ["#2e59d9", "#17a673"],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: "#000",
        borderWidth: 1,
        cornerRadius: 4,
        displayColors: true,
      },
    },
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading analytics data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="mb-4">
        <Card.Header className="bg-danger text-white">
          <h5 className="mb-0">Error Loading Analytics</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">
            <p className="mb-0">{error}</p>
          </Alert>
          <Button variant="primary" onClick={fetchAnalyticsData}>
            Retry
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow mb-4">
      <Card.Header className="py-3">
        <h4 className="m-0 font-weight-bold text-primary">
          Repeat Service Analytics
        </h4>
      </Card.Header>
      <Card.Body>
        {/* Filters */}
        <Card className="mb-4">
          <Card.Header>
            <h6 className="m-0 font-weight-bold text-primary">Filters</h6>
          </Card.Header>
          <Card.Body>
            <Form>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3 position-relative">
                    <Form.Label>Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      name="pincode"
                      value={filters.pincode}
                      onChange={handleFilterChange}
                      onBlur={handleFilterBlur}
                      onKeyPress={(e) => {
                        // Only allow numbers
                        if (
                          !/[0-9]/.test(e.key) &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight"
                        ) {
                          e.preventDefault();
                        }
                        // Apply filters when user presses Enter
                        if (e.key === "Enter") {
                          handleKeyPress(e);
                        }
                      }}
                      placeholder="Enter pincode"
                      maxLength={10}
                    />
                    {showPincodeSuggestions && filters.pincode && (
                      <div
                        className="position-absolute bg-white border rounded mt-1 w-100"
                        style={{
                          zIndex: 1000,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {pincodeSuggestions
                          .filter((pincode) =>
                            pincode
                              .toString()
                              .startsWith(filters.pincode.toString())
                          )
                          .slice(0, 10)
                          .map((pincode, index) => (
                            <div
                              key={index}
                              className="px-3 py-2"
                              onClick={() =>
                                handlePincodeSuggestionClick(pincode)
                              }
                              style={{ cursor: "pointer" }}
                              onMouseEnter={(e) =>
                                e.target.classList.add("bg-light")
                              }
                              onMouseLeave={(e) =>
                                e.target.classList.remove("bg-light")
                              }
                            >
                              {pincode}
                            </div>
                          ))}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3 position-relative">
                    <Form.Label>Society Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="society"
                      value={filters.society}
                      onChange={handleFilterChange}
                      onBlur={handleFilterBlur}
                      onKeyPress={handleKeyPress}
                      placeholder="Enter society name"
                    />
                    {showSocietyNameSuggestions && filters.society && (
                      <div
                        className="position-absolute bg-white border rounded mt-1 w-100"
                        style={{
                          zIndex: 1000,
                          maxHeight: "200px",
                          overflowY: "auto",
                        }}
                      >
                        {societyNameSuggestions
                          .filter((societyName) =>
                            societyName
                              .toLowerCase()
                              .includes(filters.society.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((societyName, index) => (
                            <div
                              key={index}
                              className="px-3 py-2"
                              onClick={() =>
                                handleSocietyNameSuggestionClick(societyName)
                              }
                              style={{ cursor: "pointer" }}
                              onMouseEnter={(e) =>
                                e.target.classList.add("bg-light")
                              }
                              onMouseLeave={(e) =>
                                e.target.classList.remove("bg-light")
                              }
                            >
                              {societyName}
                            </div>
                          ))}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Service Type</Form.Label>
                    <Form.Select
                      name="serviceType"
                      value={filters.serviceType}
                      onChange={handleFilterChange}
                    >
                      <option value="all">All Service Types</option>
                      {serviceTypes.map((type, index) => (
                        <option key={index} value={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <div className="d-flex gap-2">
                    <Button variant="primary" onClick={handleApplyFilters}>
                      Apply Filters
                    </Button>
                    <Button variant="secondary" onClick={handleResetFilters}>
                      Reset
                    </Button>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <div className="small text-muted">
                    Note: Filters will only apply when you click "Apply Filters"
                    or press Enter.
                  </div>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>

        {/* Summary Cards */}
        <Row>
          <Col md={3}>
            <Card className="shadow mb-4 border-left-primary">
              <Card.Body>
                <Row className="no-gutters align-items-center">
                  <Col className="mr-2">
                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                      Total Vendors
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {analyticsData.totalVendors}
                    </div>
                  </Col>
                  <Col className="col-auto">
                    <i className="fas fa-store fa-2x text-gray-300"></i>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3}>
            <Card className="shadow mb-4 border-left-success">
              <Card.Body>
                <Row className="no-gutters align-items-center">
                  <Col className="mr-2">
                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                      Total Services
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {analyticsData.totalServices}
                    </div>
                  </Col>
                  <Col className="col-auto">
                    <i className="fas fa-concierge-bell fa-2x text-gray-300"></i>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3}>
            <Card className="shadow mb-4 border-left-info">
              <Card.Body>
                <Row className="no-gutters align-items-center">
                  <Col className="mr-2">
                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                      Repeat Customers
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {analyticsData.totalRepeatCustomers}
                    </div>
                  </Col>
                  <Col className="col-auto">
                    <i className="fas fa-redo fa-2x text-gray-300"></i>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>

          <Col md={3}>
            <Card className="shadow mb-4 border-left-warning">
              <Card.Body>
                <Row className="no-gutters align-items-center">
                  <Col className="mr-2">
                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                      New Customers
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {analyticsData.totalNewCustomers}
                    </div>
                  </Col>
                  <Col className="col-auto">
                    <i className="fas fa-user-plus fa-2x text-gray-300"></i>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Chart and Top Vendors */}
        <Row>
          <Col md={6}>
            <Card className="shadow mb-4">
              <Card.Header className="py-3">
                <h6 className="m-0 font-weight-bold text-primary">
                  Repeat Service Distribution
                </h6>
              </Card.Header>
              <Card.Body>
                <div style={{ height: "300px" }}>
                  <Doughnut data={chartData} options={chartOptions} />
                </div>
                <div className="text-center mt-3">
                  <p className="text-muted">
                    Visual representation of repeat vs new services across all
                    vendors
                  </p>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card className="shadow mb-4">
              <Card.Header className="py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  Top Vendors by Repeat Ratio
                </h6>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={downloadTopVendorsExcel}
                >
                  <i className="fas fa-download me-1"></i> Download Excel
                </Button>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        <th>Society</th>
                        <th>Pincode</th>
                        <th>Category</th>
                        <th>Services</th>
                        <th>Repeat Count</th>
                        <th>Repeat %</th>
                        <th>Repeating Services</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analyticsData.vendors
                        .filter((vendor) => {
                          // If service type filter is applied, only show vendors with bookings
                          if (
                            appliedFilters.serviceType &&
                            appliedFilters.serviceType !== "all"
                          ) {
                            return vendor.totalServices > 0;
                          }
                          return true;
                        })
                        .sort((a, b) => b.repeatRatio - a.repeatRatio)
                        .slice(0, 5)
                        .map((vendor, index) => (
                          <tr key={vendor.id || index}>
                            <td>
                              {vendor.businessName ||
                                vendor.name ||
                                vendor.vendor?.personal_info?.name ||
                                vendor.vendor?.businessName ||
                                "N/A"}
                            </td>
                            <td>
                              {vendor.societyName ||
                                vendor.vendor?.personal_info?.societyName ||
                                "N/A"}
                            </td>
                            <td>
                              {vendor.pincode ||
                                vendor.vendor?.personal_info?.pincode ||
                                "N/A"}
                            </td>
                            <td>
                              {vendor.category ||
                                vendor.businessCategory ||
                                vendor.vendor?.categories?.[0]?.category_name ||
                                "General"}
                            </td>
                            <td>{vendor.totalServices}</td>
                            <td>{vendor.repeatCustomers}</td>
                            <td>
                              <Badge
                                bg={
                                  vendor.repeatRatio > 50
                                    ? "success"
                                    : "warning"
                                }
                              >
                                {vendor.repeatRatio}%
                              </Badge>
                            </td>
                            <td>
                              {vendor.repeatingServices &&
                              vendor.repeatingServices.length > 0 ? (
                                <div>
                                  {vendor.repeatingServices.map(
                                    (service, idx) => (
                                      <div key={idx} className="small">
                                        <strong>{service.name}</strong> (
                                        {service.count})
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted">None</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* All Vendors Table */}
        <Row>
          <Col md={12}>
            <Card className="shadow mb-4">
              <Card.Header className="py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-primary">
                  All Vendors Analytics
                </h6>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={downloadAllVendorsExcel}
                >
                  <i className="fas fa-download me-1"></i> Download Excel
                </Button>
              </Card.Header>
              <Card.Body>
                {analyticsData.vendors.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">
                      No vendors found matching the current filters.
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Vendor Name</th>
                          <th>Society Name</th>
                          <th>Pincode</th>
                          <th>Category</th>
                          <th>Total Services</th>
                          <th>Repeat Count</th>
                          <th>Repeat %</th>
                          <th>Repeating Services</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.vendors
                          .filter((vendor) => {
                            // If service type filter is applied, only show vendors with bookings
                            if (
                              appliedFilters.serviceType &&
                              appliedFilters.serviceType !== "all"
                            ) {
                              return vendor.totalServices > 0;
                            }
                            return true;
                          })
                          .map((vendor, index) => (
                            <tr key={vendor.id || index}>
                              <td>
                                {vendor.businessName ||
                                  vendor.name ||
                                  vendor.vendor?.personal_info?.name ||
                                  vendor.vendor?.businessName ||
                                  "N/A"}
                              </td>
                              <td>
                                {vendor.societyName ||
                                  vendor.vendor?.personal_info?.societyName ||
                                  "N/A"}
                              </td>
                              <td>
                                {vendor.pincode ||
                                  vendor.vendor?.personal_info?.pincode ||
                                  "N/A"}
                              </td>
                              <td>
                                {vendor.category ||
                                  vendor.businessCategory ||
                                  vendor.vendor?.categories?.[0]
                                    ?.category_name ||
                                  "General"}
                              </td>
                              <td>{vendor.totalServices}</td>
                              <td>{vendor.repeatCustomers}</td>
                              <td>
                                <Badge
                                  bg={
                                    vendor.repeatRatio > 50
                                      ? "success"
                                      : vendor.repeatRatio > 30
                                      ? "warning"
                                      : "info"
                                  }
                                >
                                  {vendor.repeatRatio}%
                                </Badge>
                              </td>
                              <td>
                                {vendor.repeatingServices &&
                                vendor.repeatingServices.length > 0 ? (
                                  <div>
                                    {vendor.repeatingServices.map(
                                      (service, idx) => (
                                        <div key={idx} className="small">
                                          <strong>{service.name}</strong> (
                                          {service.count} bookings,{" "}
                                          {service.repeatRatio}%)
                                        </div>
                                      )
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted">
                                    No repeating services
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default RepeatServiceAnalytics;
